import { useEffect, useRef, useState } from "react";

import { useCurrentWorkspace } from "@accurx/auth";
import { useBrowserEnvironment } from "@accurx/native";
import { generatePath, useHistory } from "react-router-dom";

import { ROUTES_COMPOSE } from "../../Routes.constants";
import { useWindowOpenCloseEvents } from "../hooks/useWindowOpenCloseEvents";

export const WAIT_FOR_PATIENT_DELAY = 4000;

/**
 * useCurrentPatientDelay is used to add an artificial delay to the current
 * patient being fetched from the medical record. When we open Compose shortly
 * after selecting a patient in the EMR, there's a period of time when it appears
 * there's no patient selected, while we wait for an update form the EMR.
 *
 * If we don't delay, we'll end up on the patient search page and the user will
 * have to manually select the patient. Adding the delay holds the user in the
 * loading state for longer.
 *
 * When the window is closed this hook will return `true` and will remain `true`
 * once the window is opened again until either:
 *  - `hasPatientFeedPatient`, becomes `true`
 *  - The hook has waited the specified time.
 *
 * @param hasPatientFeedPatient Whether or not the current patient feed includes
 * a patient (is not `null`).
 * @returns whether or not to hold the user in the loading state
 */
export const useCurrentPatientDelay = (hasPatientFeedPatient: boolean) => {
    const browserEnv = useBrowserEnvironment();

    const [currentPatientDelay, setCurrentPatientDelay] = useState(
        !hasPatientFeedPatient && browserEnv === "WebView",
    );
    const timerRef = useRef<number | null>(null);

    const history = useHistory();
    const { orgId: workspaceId } = useCurrentWorkspace();
    const [isOpen, setIsOpen] = useState(true);

    useWindowOpenCloseEvents({
        onOpen: () => {
            setIsOpen(true);
            timerRef.current = window.setTimeout(() => {
                setCurrentPatientDelay(false);
                timerRef.current = null;
            }, WAIT_FOR_PATIENT_DELAY);
        },
        onClose: () => {
            // Clear the timeout in case the user closes the window before
            // the timeout has been cleared, so that a new one can be set
            // when the window is opened again.
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            setIsOpen(false);
            setCurrentPatientDelay(true);
            history.replace(
                generatePath(ROUTES_COMPOSE.compose, { workspaceId }),
            );
        },
    });

    useEffect(() => {
        if (hasPatientFeedPatient && timerRef.current && isOpen) {
            setCurrentPatientDelay(false);
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, [hasPatientFeedPatient, isOpen]);

    useEffect(() => {
        if (currentPatientDelay) {
            // If the initial render has a current patient delay, we need a delay
            // to clear it, as an `onOpen` even is only fired when the window is
            // re-opened.
            timerRef.current = window.setTimeout(() => {
                setCurrentPatientDelay(false);
                timerRef.current = null;
            }, WAIT_FOR_PATIENT_DELAY);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want this effect to run on mount and to provide the unmount behaviour
    }, []);

    return currentPatientDelay;
};
